import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function TableNo32() {
  const pdfRef = useRef();

  // 📊 Data
  const educationGenderShareData = [
    {
      educationLevel: "High school or equivalent, no college",
      femaleShare2018: 24.39,
      maleShare2018: 24.8,
      femaleShare2024: 22.83,
      maleShare2024: 24.36,
    },
  ];

  // 🧮 Change calculation
  const dataWithChange = educationGenderShareData.map((item) => ({
    ...item,
    femaleChange: (item.femaleShare2024 - item.femaleShare2018).toFixed(2),
    maleChange: (item.maleShare2024 - item.maleShare2018).toFixed(2),
  }));

  const femaleTrend =
    dataWithChange[0].femaleChange > 0 ? "increased" : "decreased";
  const maleTrend =
    dataWithChange[0].maleChange > 0 ? "increased" : "decreased";

  // 📈 Chart Data
  const chartData = {
    labels: educationGenderShareData.map((item) => item.educationLevel),
    datasets: [
      {
        label: "Female 2018 (%)",
        data: educationGenderShareData.map((item) => item.femaleShare2018),
        backgroundColor: "#EC4899",
        borderRadius: 5,
      },
      {
        label: "Male 2018 (%)",
        data: educationGenderShareData.map((item) => item.maleShare2018),
        backgroundColor: "#0ea5e9",
        borderRadius: 5,
      },
      {
        label: "Female 2024 (%)",
        data: educationGenderShareData.map((item) => item.femaleShare2024),
        backgroundColor: "#EC4899",
        borderRadius: 5,
      },
      {
        label: "Male 2024 (%)",
        data: educationGenderShareData.map((item) => item.maleShare2024),
        backgroundColor: "#0ea5e9",
        borderRadius: 5,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "👩‍🎓 Education Level Share by Gender (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw}%`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: "#4B5563", callback: (v) => v + "%" },
        title: {
          display: true,
          text: "Share Percentage (%)",
          color: "#111827",
          font: { size: 14, weight: "bold" },
        },
      },
      x: {
        ticks: { color: "#4B5563" },
      },
    },
  };

  // 📄 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("education_gender_share.pdf");
      // input.style.width = originalWidth;
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">
          Education Level Share by Gender (2018–2024)
        </h2>
        <p className="text-center text-gray-600 mb-6">
          Comparison of male and female workforce shares for individuals with a
          high school education or equivalent in Utah, showing change between
          2018 and 2024.
        </p>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Female Share Change
            </p>
            <p
              className={`text-lg font-bold ${
                dataWithChange[0].femaleChange > 0
                  ? "text-green-700"
                  : "text-red-700"
              }`}
            >
              {dataWithChange[0].femaleChange > 0 ? "+" : ""}
              {dataWithChange[0].femaleChange}% ({femaleTrend})
            </p>
          </div>
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Male Share Change
            </p>
            <p
              className={`text-lg font-bold ${
                dataWithChange[0].maleChange > 0
                  ? "text-green-700"
                  : "text-red-700"
              }`}
            >
              {dataWithChange[0].maleChange > 0 ? "+" : ""}
              {dataWithChange[0].maleChange}% ({maleTrend})
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-blue-700 text-white">
                <tr>
                  <th className="p-2">Education Level</th>
                  <th className="p-2">Female 2018</th>
                  <th className="p-2">Male 2018</th>
                  <th className="p-2">Female 2024</th>
                  <th className="p-2">Male 2024</th>
                  <th className="p-2">Δ Female</th>
                  <th className="p-2">Δ Male</th>
                </tr>
              </thead>
              <tbody>
                {dataWithChange.map((item, i) => (
                  <tr key={i} className="hover:bg-gray-100 transition">
                    <td className="p-2 font-medium">{item.educationLevel}</td>
                    <td className="p-2">{item.femaleShare2018}%</td>
                    <td className="p-2">{item.maleShare2018}%</td>
                    <td className="p-2">{item.femaleShare2024}%</td>
                    <td className="p-2">{item.maleShare2024}%</td>
                    <td
                      className={`p-2 font-semibold ${
                        item.femaleChange > 0
                          ? "text-green-700"
                          : "text-red-700"
                      }`}
                    >
                      {item.femaleChange > 0 ? "+" : ""}
                      {item.femaleChange}%
                    </td>
                    <td
                      className={`p-2 font-semibold ${
                        item.maleChange > 0 ? "text-green-700" : "text-red-700"
                      }`}
                    >
                      {item.maleChange > 0 ? "+" : ""}
                      {item.maleChange}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div style={{ height: "400px", width: "100%" }}>
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          📉 Between 2018 and 2024, the female share{" "}
          <span
            className={`font-semibold ${
              dataWithChange[0].femaleChange > 0
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {femaleTrend} by {Math.abs(dataWithChange[0].femaleChange)}%
          </span>{" "}
          while the male share{" "}
          <span
            className={`font-semibold ${
              dataWithChange[0].maleChange > 0
                ? "text-green-700"
                : "text-red-700"
            }`}
          >
            {maleTrend} by {Math.abs(dataWithChange[0].maleChange)}%
          </span>
          .
        </p>
      </div>

      {/* PDF Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
